import React from 'react';

import { DeliveryEventMessage } from 'src/components/atoms/DeliveryEventMessage/DeliveryEventMessage';
import { FreePartnerDeliveryMessage } from 'src/components/atoms/FreePartnerDeliveryMessage/FreePartnerDeliveryMessage';
import type { DeliveryEvent, DeliveryOption } from 'src/redux/delivery/types';
import { deliveryOptionArrivesOnOrByDate } from 'src/redux/delivery/utils/deliveryOptionArrivesOnOrByDate';
import { getCutoffMessage } from 'src/redux/delivery/utils/getCutoffMessage';
import { getDeliveryOptionText } from 'src/redux/delivery/utils/getDeliveryOptionText';
import { getFormattedDeliveryDate } from 'src/redux/delivery/utils/getFormattedDeliveryDate';
import { getFormattedPrice } from 'src/redux/delivery/utils/getFormattedPrice';
import * as styles from './DeliveryOptions.styles';

interface DeliveryOptionsProps {
  deliveryEvent?: DeliveryEvent;
  deliveryOptions: DeliveryOption[] | [];
  partnerName: string;
  specialSize?: boolean;
}

export const DeliveryOptions = ({
  deliveryEvent,
  deliveryOptions,
  partnerName,
  specialSize = false,
}: DeliveryOptionsProps) => {
  const hasOptionThatArrivesByEvent = deliveryEvent
    ? deliveryOptions.some((option) => deliveryOptionArrivesOnOrByDate(option, deliveryEvent.date))
    : false;
  const hasFreeOption = deliveryOptions.some((option) => Number(option.price.amount) === 0);

  return (
    <>
      {deliveryEvent && hasOptionThatArrivesByEvent && (
        <DeliveryEventMessage eventName={deliveryEvent.name} />
      )}
      <ul css={styles.deliveryOptionsList}>
        {deliveryOptions.map((deliveryOption) => {
          const {
            cutoffDateTime,
            date,
            exactDate,
            max,
            min,
            optionCode,
            partnerFreeDeliveryThresholdPrice,
            preSalePrice,
            price: { amount, currencyCode },
          } = deliveryOption;
          const optionPrice = Number(amount);
          const cutoffMessage = getCutoffMessage(cutoffDateTime);
          const formattedPrice = getFormattedPrice(optionPrice, currencyCode);
          const showArrivesBy = deliveryEvent
            ? deliveryOptionArrivesOnOrByDate(deliveryOption, deliveryEvent.date)
            : false;

          return (
            <li css={styles.item} key={optionCode}>
              <div css={styles.optionInfo}>
                <span>
                  <h6 css={styles.optionHeading}>
                    {exactDate && date ? (
                      <b>
                        {optionCode === 'SATURDAY' ? 'Get it on ' : 'Estimated delivery '}
                        <time dateTime={date}>{getFormattedDeliveryDate(date)}</time>
                      </b>
                    ) : (
                      `${min}-${max} days`
                    )}
                    {showArrivesBy ? (
                      <span>
                        {' '}
                        · Arrives in time for{' '}
                        <time dateTime={deliveryEvent!.date}>{deliveryEvent!.name}</time>
                      </span>
                    ) : null}
                  </h6>
                  <p css={styles.optionBody}>
                    {cutoffMessage ? `Order by ${cutoffMessage} · ` : ''}
                    {specialSize ? 'Special size delivery' : getDeliveryOptionText(optionCode)}
                  </p>
                </span>
                <span css={styles.optionPrices}>
                  {preSalePrice && (
                    <span>
                      {getFormattedPrice(Number(preSalePrice.amount), preSalePrice.currencyCode)}
                    </span>
                  )}
                  <b css={styles.optionPrice} data-price={optionPrice}>
                    {formattedPrice}
                  </b>
                </span>
              </div>
              {partnerFreeDeliveryThresholdPrice && !hasFreeOption && (
                <div css={styles.promotionContainer} data-testid="delivery-promo-container">
                  <FreePartnerDeliveryMessage
                    formattedThreshold={getFormattedPrice(
                      Number(partnerFreeDeliveryThresholdPrice.amount),
                      partnerFreeDeliveryThresholdPrice.currencyCode,
                    )}
                    partnerName={partnerName}
                    styleOverride={styles.promotionMessageOverride}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
};
